import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, X, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { imageFallback, resolveImageUrl } from '../lib/imageUtils';

const WishlistPage = () => {
  const { wishlist, toggleWishlist, addToCart } = useCart();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleAddToCart = (product) => {
    addToCart(product);
  };

  if (wishlist.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="empty-wishlist">
        <div className="text-center space-y-6 max-w-md px-6">
          <Heart size={64} className="text-[#A3A3A3] mx-auto" />
          <h2 className="text-3xl font-light tracking-tighter text-white">Your Wishlist is Empty</h2>
          <p className="text-[#A3A3A3]">Save your favorite pieces for later</p>
          <Link
            to="/products"
            className="inline-block px-10 py-4 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-all duration-300"
            data-testid="browse-products-button"
          >
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white mb-2" data-testid="wishlist-page-title">
            My Wishlist
          </h1>
          <p className="text-[#A3A3A3]">{wishlist.length} {wishlist.length === 1 ? 'item' : 'items'}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {wishlist.map((product) => {
            const imageUrl = resolveImageUrl(product.images?.[0], BACKEND_URL);

            const hasDiscount = product.offer_applied || product.final_price < product.price;

            return (
              <div
                key={product.id}
                className="bg-[#141414] border border-white/10 overflow-hidden group"
                data-testid={`wishlist-item-${product.id}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <Link to={`/products/${product.id}`}>
                    <img
                      src={imageUrl}
                      alt={product.name}
                      onError={imageFallback}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </Link>
                  
                  {product.stock === 0 && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <span className="text-white uppercase tracking-wider text-sm">Out of Stock</span>
                    </div>
                  )}

                  <button
                    onClick={() => toggleWishlist(product)}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-colors"
                    data-testid={`remove-from-wishlist-${product.id}`}
                  >
                    <X size={18} className="text-white" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                      {product.subcategory_name || product.category_name || 'Jewelry'}
                    </p>
                    <Link to={`/products/${product.id}`}>
                      <h3 className="text-xl font-light text-white tracking-tight hover:text-[#D4AF37] transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                  </div>

                  <div className="flex items-baseline space-x-3">
                    <span className="text-2xl font-light text-[#D4AF37]">
                      ₹{product.final_price.toLocaleString()}
                    </span>
                    {hasDiscount && (
                      <span className="text-sm text-[#A3A3A3] line-through">
                        ₹{product.price.toLocaleString()}
                      </span>
                    )}
                  </div>

                  <button
                    onClick={() => handleAddToCart(product)}
                    disabled={product.stock === 0}
                    className="w-full px-6 py-3 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    data-testid={`add-to-cart-wishlist-${product.id}`}
                  >
                    <ShoppingCart size={16} />
                    <span>{product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WishlistPage;
